import path from 'node:path';
import { SpanStatusCode } from '@opentelemetry/api';
import {
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_HTTP_ROUTE,
} from '@opentelemetry/semantic-conventions';
import * as Sentry from '@sentry/nextjs';
import Bubble from 'components/bubble';
import CTA from 'components/Campaign/CTA';
import { CAMPAIGN_TIERS } from 'components/Campaign/config';
import Layout, { DATA_TYPES as SEO_DATA_TYPES } from 'components/layout';
import ProgressBar from 'components/progressBar';
import ShareBlock from 'components/Sharing';
import Anchor from 'components/typography/anchor';
import { format, getDate, isThisYear, parseISO } from 'date-fns';
import driveShareImg from 'images/shares/drives.png';
import { getUserSharecodeForId } from 'lib/api/petitions';
import { getDrive } from 'lib/api/themis/queries';
import { otelApi } from 'lib/instrumentation'; // Initializes instrumentation
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import config, { DATA_TYPES } from 'siteconfig';
import slugify from 'slugify';
import { getClosureDateForDrives } from 'utilities/dates';
import { getOpenGraphUrl } from 'utilities/paths';
import { DriveType } from 'utilities/propTypes';
import { getStateProperName } from 'utilities/states';

export const getServerSideProps = Sentry.wrapGetServerSidePropsWithSentry(
  async ({ req, params }) => {
    const parentCtx = otelApi.context.active();
    const parentSpan = otelApi.trace
      .getTracer('Gin Rickey')
      .startSpan('drives/[usercode]', undefined, parentCtx);

    parentSpan.setAttributes({
      // https here is unused, only need it to split the URL open
      [ATTR_HTTP_ROUTE]: new URL(req.url, `https://${req.headers.host}`)
        .pathname,
    });

    const shareCode = params.sharecode.toUpperCase();
    const drive = await getDrive({ shareCode });

    if (!drive) {
      parentSpan.setStatus({
        code: SpanStatusCode.OK,
      });
      parentSpan.setAttributes({
        [ATTR_HTTP_RESPONSE_STATUS_CODE]: 404,
      });
      parentSpan.end();
      return {
        notFound: true,
      };
    }

    let userShareCode = null;
    if (drive.userId) {
      userShareCode = await getUserSharecodeForId({ userId: drive.userId });
    }

    const isClosed =
      drive.election.date < getClosureDateForDrives({ now: new Date() });

    parentSpan.end();
    return {
      props: { drive, userShareCode, isClosed },
    };
  }
);

const SHORT_DATE = 'MMMM d';
const LONG_DATE = 'MMMM d, uuuu';

const CampaignMetadata = ({ dateString, userCode }) => {
  const nameLink = userCode ? (
    <>
      <em>by</em>{' '}
      <Anchor href={`/go/${userCode.toLowerCase()}`}>{`@${userCode}`}</Anchor>
    </>
  ) : null;

  return (
    <p className="mb-9 text-sm dark:border-gray-700 print:hidden">
      Started <em>on</em> {dateString} {nameLink}
    </p>
  );
};

const Drive = ({ drive, userShareCode, isClosed }) => {
  const router = useRouter();
  const url = router.asPath;
  const TYPE = DATA_TYPES.DRIVE;
  const { title: subject, body, shareCode, totalSigns } = drive;

  // If election is this year, we do `November 8`. If a different year, `November 8, 2023`
  const electionDate = parseISO(drive.election.date);
  const createdDate = parseISO(drive.created);

  const createdDateFormatted = isThisYear(electionDate)
    ? format(createdDate, SHORT_DATE)
    : format(createdDate, LONG_DATE);

  const officeName = drive.office.name;
  const candidateName = drive.candidate.fullName;
  const partyAbbrev = drive.candidate.partyName.substring(0, 3);

  const incumbentType = drive.candidate.isIncumbent
    ? 'Incumbent'
    : 'Challenger';
  const candidateUrl = drive.candidate.url;
  const officeUrl = drive.office.url;
  const raceUrl = drive.race.url;

  const dynamicOpenGraphUrlHref = getOpenGraphUrl('/api/og/drive/drive', {
    electionDate: format(electionDate, LONG_DATE),
    shareCode,
    subject,
  });

  return (
    <Layout
      title={`Resistbot Vote Drive: ${candidateName} for ${officeName}`}
      ogTitle={subject}
      dataType={SEO_DATA_TYPES.DRIVE}
      description={`Text Endorse ${shareCode} to 50409 to show your support.`}
      image={driveShareImg}
      dynamicImage={dynamicOpenGraphUrlHref}
      imageAltText='Illustration of a bot voting with overlay text that reads, "Vote Drives: Endorse your candidate and get out the vote before Election Day. Text drive to 50409.'
      canonicalPath={`/${TYPE.basePath}/${shareCode}`}
    >
      <div className="mb-9 flex flex-col flex-nowrap gap-2 md:gap-4 md:text-center">
        <ol className="mb-4 flex list-none flex-wrap items-center gap-2 p-0 text-sm">
          <li>
            <Anchor href="/drives">United States</Anchor>
          </li>
          <li>
            <Anchor href={`/drives/explore/${drive.district.state}`}>
              {getStateProperName(drive.district.state)}
            </Anchor>
          </li>
          <li>
            <Anchor
              href={path.join(
                DATA_TYPES.CANDIDATE.basePath,
                `${drive.candidate.id}/${slugify(drive.candidate.fullName)}`
              )}
            >
              {drive.candidate.lastName}
            </Anchor>
          </li>
          <li>{shareCode}</li>
        </ol>
        <h1>{subject}</h1>

        {/* Progress Bar */}
        {!isClosed ? (
          <div className="md:mx-auto md:w-4/5">
            <ProgressBar
              current={totalSigns}
              tiers={CAMPAIGN_TIERS}
              label={'endorsements'}
            />
          </div>
        ) : null}
      </div>

      <div className="flex flex-row flex-wrap gap-9 print:flex-col-reverse">
        <div className="min-w-[calc(59%-2.25rem)] flex-2">
          <div className="flex">
            <div>
              <h2>
                <Anchor href={`${candidateUrl}?utm_source=resistbot`}>
                  {candidateName}
                </Anchor>{' '}
                <span
                  className={
                    partyAbbrev === 'Dem'
                      ? 'relative -top-0.5 ml-1 inline-block rounded bg-blue-400 px-0.5 text-xs leading-[14px] font-bold text-white uppercase'
                      : 'relative -top-0.5 ml-1 inline-block rounded bg-gray-700 px-0.5 text-xs leading-[14px] font-bold text-white uppercase'
                  }
                >
                  {partyAbbrev}
                </span>
              </h2>
              <p className="text-xs font-semibold tracking-wider uppercase md:text-sm md:leading-tight">
                <Anchor href={`${raceUrl}?utm_source=resistbot`}>
                  {incumbentType}
                </Anchor>{' '}
                •{' '}
                <Anchor href={`${officeUrl}?utm_source=resistbot`}>
                  {officeName}
                </Anchor>
              </p>
            </div>
            <div className="mb-6 ml-6 h-[74px] w-[68px] flex-shrink-0 overflow-hidden rounded text-center shadow-sm">
              <div className="h-[30px] border-b border-gray-300 bg-gray-700 text-base leading-[30px] text-white uppercase">
                {format(electionDate, 'LLL')}
              </div>
              <div className="text-[36px] leading-[44px]">
                {getDate(electionDate)}
              </div>
            </div>
          </div>

          <p className="print:box-orient-vertical prose prose-lg mb-8 dark:prose-invert print:mb-0 print:line-clamp-18 print:flex print:max-h-[5in] print:overflow-hidden print:pb-4.5 print:text-ellipsis">
            {body}
          </p>

          <CampaignMetadata
            dateString={createdDateFormatted}
            userCode={userShareCode}
          />
        </div>

        {/* CTA Steps and Buttons */}
        {isClosed ? null : (
          <div className="flex-1">
            <CTA
              shareCode={shareCode}
              type={DATA_TYPES.DRIVE}
              isClosed={isClosed}
              actionText="check your registration and endorse this candidate"
            />

            <div className="text-sm leading-tight print:hidden">
              <h3>Already endorsed?</h3>

              <ul className="my-4 mb-6 pl-5">
                <li>
                  Text <Bubble>INVITE {shareCode}</Bubble> to send text and
                  e-mail invitations to your friends.
                </li>

                <li>
                  Text <Bubble>PROMOTE {shareCode}</Bubble> to have this drive
                  texted to other members in 
                  {getStateProperName(drive.district.state)}.
                </li>

                <li>
                  Print this and post around campus or on your community
                  bulletin&nbsp;board!
                </li>

                <li>
                  Join our{' '}
                  <Anchor href={config.socialUrls.discord}>Discord</Anchor> and
                  connect with fellow organizers
                </li>
              </ul>

              <ShareBlock
                pathname={url}
                text={`Text ENDORSE ${shareCode} to 50409`}
              />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

Drive.propTypes = {
  drive: DriveType,
  userShareCode: PropTypes.string,
};

export default Drive;
